import { startOfDay, endOfDay } from 'date-fns';
import { getEventDescription } from '#component/shipment/log/helper';
import { shipmentEventType } from '#type';

export const getShipmentDateRange = shipment => {
  const delivery = shipment.states ?? shipment.delivery ?? [];

  const currentDate = new Date().toISOString();
  const lastState = delivery.length > 1 ? delivery[delivery.length - 1] : {};
  const firstState = delivery?.[0] || {};
  const rangeStart = firstState.date ?
    firstState.done ?
      firstState.done < firstState.date ? firstState.done : firstState.date :
      firstState.date :
    shipment.creationDate;
  let rangeEnd;

  if (shipment.done) {
    rangeEnd = shipment.done;
    // the Destination checkpoint without a location should limit the data by the Expected date
    if (lastState.date && lastState.done &&
      !(lastState.address || lastState.locations)) {
      rangeEnd = lastState.date;
    }
  } else if (lastState.date) {
    rangeEnd = lastState.date > currentDate ? currentDate : lastState.date;
  } else {
    rangeEnd = rangeStart > currentDate ? rangeStart : currentDate;
  }

  const minRangeDiffH = 2; // hours
  const minRangeDiff = minRangeDiffH * 1000 * 60 * 60; // milliseconds
  if (+new Date(rangeEnd) - new Date(rangeStart) < minRangeDiff) {
    rangeEnd = new Date(rangeStart);
    rangeEnd.setHours(rangeEnd.getHours() + minRangeDiffH);
    rangeEnd = rangeEnd.toISOString();
  }

  const rangeInterval = Math.round((new Date(rangeEnd) - new Date(rangeStart)) / 8 / 1000);

  return { rangeStart, rangeEnd, rangeInterval };
};

export const getLastActiveDevice = devices => {
  const devicesWithLastMetric = devices.map(device => {
    const devWithLastMetric = { ...device,
      lastMetric: {
        ...device.locations?.[device.locations?.length ?? 0 - 1]?.metrics,
        abs: device.metrics?.shock?.[device.metrics?.shock?.length ?? 0 - 1]?.abs
      } };
    return devWithLastMetric;
  });

  const lastActiveDevice = devicesWithLastMetric?.reduce((prev, current) =>
    ((new Date(prev.lastMetric?.timestamp) > new Date(current.lastMetric?.timestamp || 1970)) ?
      { ...prev,
        lastMetric: { ...prev.lastMetric,
          abs: prev.lastMetric?.abs || current.lastMetric?.abs
        }
      } :
      { ...current,
        lastMetric: { ...current.lastMetric,
          abs: current.lastMetric?.abs || prev.lastMetric?.abs
        }
      }
    ), {});

  return lastActiveDevice;
};

export const getChartRange = item => {
  if (Array.isArray(item)) {
    return [
      startOfDay(new Date(item[0].timestamp)),
      endOfDay(new Date(item[0].timestamp))
    ];
  }

  return new Date(item.timestamp);
};

const formatLog = log => {
  const { event } = log;
  const eventId = event?.id;
  const timestamp =
    shipmentEventType.TRIGGER_ALERT === eventId && log.messageTimestamp ?
      log.messageTimestamp : log.timestamp;

  return {
    details: log,
    timestamp: `${timestamp}`,
    description: getEventDescription(log, '', true),
    identifier: `shipment_${event?.id}`,
    eventId
  };
};

export const mergeEvents = events =>
  events
    .reduce((a, c) => {
      const isDeviceEvent = Object.prototype.hasOwnProperty.call(c, 'id');
      if (isDeviceEvent) {
        a.push(...c.events);
      } else {
        a.push(formatLog(c));
      }

      return a;
    }, [])
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .reverse();

export const isIdentifiersLoaded = (identifiers, state) => {
  const loadingIdentifiers = identifiers.reduce((a, c) => {
    a.push(`${c}Loading`);
    return a;
  }, []);

  const isMetricLoading = Object.keys(state).find(k =>
    loadingIdentifiers.includes(k) && state[k]);

  return identifiers?.length ? !isMetricLoading : true;
};

export const getEventsWithAcceptance = (events, currentEvent, reason) => {
  const currentLogId = currentEvent?.details?.logId;

  const newEvents = events.map(event => {
    if (event?.details?.logId === currentLogId) {
      if (reason) {
        return {
          ...event,
          details: {
            ...event.details,
            acceptance: {
              value: reason
            }
          }
        };
      }

      delete event.details.acceptance;
      return event;
    }
    return event;
  });

  return newEvents;
};

export const shouldShowAxisInfo = tabName => {
  const tabsRequiringAxisInfo = ['acceleration', 'orientation'];
  return tabsRequiringAxisInfo.includes(tabName);
};

const addAxisInfo = (devices, clickedAxis) => devices?.map(device => ({
  ...device,
  selectedAxis: clickedAxis?.find(ca => ca.uid === device.uid)?.axis
}));

const removeAxisInfo = devices => devices.map(d => {
  const { tinted, ...rest } = d;
  return rest;
});

export const getDevicesWithAxis = (devices, clickedAxis, tabName) => {
  const showAxisInfo = shouldShowAxisInfo(tabName, clickedAxis);

  if (showAxisInfo && clickedAxis?.length) {
    return addAxisInfo(devices, clickedAxis);
  }

  return removeAxisInfo(devices);
};
